import { Step } from './../components/_common/steppers/steps/step';
import { observable, action, computed } from 'mobx';
import { KycStatus } from '../models/dtos/kyc';
import { KycApi } from '../apis/kycApi';
import { CompanyStore } from './companyStore';
import { isKycFormRequired } from '../utils/kycGate';

export interface PageStep {
  step: Step;
  active: boolean;
  location: string;
}

// loanPartnerId: 1=Advance, 2=NetBank, 3=Unobank
const NETBANK_LOAN_PARTNER_ID = 2;

export class PageStepperStore {
  @observable
  private _kycStatus: KycStatus | null = null;

  @observable
  private _kycStatusFetched: boolean = false;

  @observable
  private _activeStepIndex: number = 0;

  private kycApi: KycApi;
  private companyStore: CompanyStore;

  constructor(companyStore: CompanyStore) {
    this.kycApi = new KycApi();
    this.companyStore = companyStore;
  }

  @computed
  get pageSteps(): PageStep[] {
    const isNetbank =
      this.companyStore.hasCompanySetting &&
      this.companyStore.loanPartnerId === NETBANK_LOAN_PARTNER_ID;

    // b.ii.2.3 AC #3/#4 — the "who still owes KYC" predicate is the shared gate
    // helper, so the decision is not encoded a second time here.
    //
    // The partner check stays: this in-flow "Verify Identity" step belongs to
    // partners that collect KYC inside the loan application. Netbank collects it
    // up front in the widget instead — with the flag on via the KYC form
    // (b.ii.2.4/2.5), and with it off Netbank never had this step — so Netbank is
    // excluded either way and non-Netbank behaviour is unchanged.
    if (!isNetbank && isKycFormRequired(this._kycStatus)) {
      return [
        {
          step: { number: 1, label: 'Amount' },
          active: this._activeStepIndex === 0,
          location: '/loan-amount-selection',
        },
        {
          step: { number: 2, label: 'Purpose' },
          active: this._activeStepIndex === 1,
          location: '/reason',
        },
        {
          step: { number: 3, label: 'Verify Identity' },
          active: this._activeStepIndex === 2,
          location: '/verify-identity',
        },
        {
          step: { number: 4, label: 'Review & Submit' },
          active: this._activeStepIndex === 3,
          location: '/agreement',
        },
      ];
    }

    return [
      {
        step: { number: 1, label: 'Amount' },
        active: this._activeStepIndex === 0,
        location: '/loan-amount-selection',
      },
      {
        step: { number: 2, label: 'Purpose' },
        active: this._activeStepIndex === 1,
        location: '/reason',
      },
      {
        step: { number: 3, label: 'Review & Submit' },
        active: this._activeStepIndex === 2,
        location: '/agreement',
      },
    ];
  }

  @action
  async fetchKycStatus(borrowerId: number): Promise<void> {
    try {
      const response = await this.kycApi.getKycStatus(borrowerId);
      this._kycStatus = response.statusId;
      this._kycStatusFetched = true;
    } catch (error) {
      console.error('Failed to fetch KYC status:', error);
      this._kycStatus = null;
      this._kycStatusFetched = true;
    }
  }

  @action
  setActiveIndex(index: number) {
    this._activeStepIndex = index;
  }

  @computed
  get kycStatus(): KycStatus | null {
    return this._kycStatus;
  }

  @computed
  get kycStatusFetched(): boolean {
    return this._kycStatusFetched;
  }
}
